<template>
	<article class="info-card" :class="variant">
		<span class="icon-slot">
			<slot name="icon" />
		</span>

		<h3>{{ title }}</h3>
		<p>{{ description }}</p>
	</article>
</template>

<script setup lang="ts">
withDefaults(
	defineProps<{
		title: string;
		description: string;
		variant?: "light" | "dark";
	}>(),
	{
		variant: "dark",
	},
);
</script>

<style scoped lang="scss">
@use "@/assets/css/vars" as *;

.info-card {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 32px;
	border-radius: 18px;
	border: 1px solid transparent;
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;

	h3 {
		margin: 0;
		font-family: $font-heading;
		font-size: $text-xl;
		font-weight: $font-semibold;
		line-height: 26px;
	}

	p {
		margin: 0;
		font-size: $text-md;
		line-height: 165%;
	}

	/* ======================================
	   ICON
	====================================== */

	.icon-slot {
		display: flex;
		align-items: center;
		justify-content: center;
		align-self: flex-start;
		flex-shrink: 0;
		border-radius: 12px;
		box-sizing: border-box;

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	/* ======================================
	   LIGHT
	====================================== */

	&.light {
		background: $text-white;

		h3 {
			color: $text-color;
		}

		p {
			color: #3b4553;
		}

		.icon-slot {
			width: 46px;
			height: 46px;
			padding: 6px;
			background: $text-white;
		}

		&:hover {
			box-shadow:
				0 0 0 1px rgba(0, 0, 0, 0.05),
				0 8px 24px rgba(0, 0, 0, 0.08);
		}
	}

	/* ======================================
	   DARK
	====================================== */

	&.dark {
		background: #0c1116;

		h3 {
			color: #fcfcfd;
		}

		p {
			color: #cdd3da;
		}

		.icon-slot {
			width: 56px;
			height: 56px;
			padding: 4px;
			padding-right: 20px;
			background: transparent;
		}

		&:hover {
			box-shadow:
				0 0 0 1px rgba(255, 255, 255, 0.06),
				0 8px 24px rgba(0, 0, 0, 0.35),
				0 0 40px color-mix(in srgb, $app-color 20%, transparent);
		}
	}
}
</style>
